import { EventType, record } from "@adora-so/rrweb";
import { IncrementalSource, MouseInteractions, } from "@adora-so/types";
import { CUSTOM_DOM_0_EVENT_HANDLERS, createCustomClickEvent, createCustomCohortAddEvent, createCustomUrlChangeEvent, } from "@adora/lib";
import { extractCustomCohortsFromQueryParams } from "./cohort-parsers/query-params";
import { debugLog } from "./debug";
import { redactURL } from "./redact";
import { urlAccepted } from "./url-accepted";
class EventStore {
    org;
    recorder;
    // This is just a rough estimate of the bite size of the serialized events.
    // It's a rough estimate because the sum of each individual event isn't the same
    // as the size of the all the events serialized together.
    events = [];
    _estimatedByteSize = 0;
    constructor(org, recorder, initialEvents) {
        this.org = org;
        this.recorder = recorder;
        this.events = initialEvents;
        this._estimatedByteSize = this.calculateTotalSize();
    }
    get estimatedByteSize() {
        return this._estimatedByteSize;
    }
    calculateTotalSize() {
        return stringByteSize(JSON.stringify(this.events));
    }
    clearEvents() {
        this.events = [];
        this._estimatedByteSize = this.calculateTotalSize();
    }
    pushEvent({ eventHref, event }) {
        if (!urlAccepted({
            urlStr: eventHref,
            pathConfig: this.org.pathConfig,
        })) {
            debugLog(`URL not accepted: ${eventHref}`);
            return;
        }
        // Sometimes events are emitted after setRecording(false) has been called on the recorder
        // in these cases just drop the events.
        if (!this.recorder.isRecording) {
            debugLog(`Dropping event - recorder is not recording`);
            return;
        }
        this.events.push(event);
        this._estimatedByteSize = this.calculateTotalSize();
    }
    getEvents() {
        return this.events;
    }
}
export class EventHolderImpl {
    org;
    eventStorage;
    getHref;
    lastHref;
    eventStore;
    constructor(org, eventStorage, getHref, recorder, initialEvents) {
        this.org = org;
        this.eventStorage = eventStorage;
        this.getHref = getHref;
        this.eventStore = new EventStore(org, recorder, initialEvents);
    }
    get estimatedSerializedByteSize() {
        return this.eventStore.estimatedByteSize;
    }
    clear() {
        this.eventStore.clearEvents();
        this.eventStorage.removeEvents();
    }
    getEvents() {
        return this.eventStore.getEvents();
    }
    onVisibilityChange(visible) {
        if (!visible) {
            this.eventStorage.setEvents(this.getEvents());
        }
    }
    append(event) {
        const eventHref = this.getHref();
        if (event.type === EventType.Meta) {
            event.data.href = redactURL(event.data.href, this.org.allowedQueryParams, this.org.pathConfig);
            this.lastHref = this.getHref();
        }
        else if (this.lastHref && eventHref != this.lastHref) {
            debugLog(`URL changed from ${this.lastHref} to ${eventHref}, adding artificial meta event for ${this.getHref()}`);
            this.eventStore.pushEvent({
                eventHref,
                event: createCustomUrlChangeEvent({
                    href: redactURL(eventHref, this.org.allowedQueryParams, this.org.pathConfig),
                    delay: event.delay,
                    timestamp: event.timestamp,
                }),
            });
            const customCohortsFromQueryParams = extractCustomCohortsFromQueryParams(eventHref, this.org.customQueryParamsCohorts);
            if (customCohortsFromQueryParams) {
                this.eventStore.pushEvent({
                    eventHref,
                    event: createCustomCohortAddEvent({
                        payload: {
                            userCohorts: customCohortsFromQueryParams,
                            source: "query_params",
                        },
                        timestamp: event.timestamp,
                        delay: event.delay,
                    }),
                });
            }
            this.lastHref = this.getHref();
        }
        if (event.type === EventType.IncrementalSnapshot) {
            if (event.data.source === IncrementalSource.MouseInteraction &&
                event.data.type === MouseInteractions.Click) {
                const node = record.mirror.getNode(event.data.id);
                if (node) {
                    const dom0EventHandlers = getDom0EventHandlers(node);
                    const customClickEvent = createCustomClickEvent({
                        dom0EventHandlers,
                        delay: event.delay,
                        timestamp: event.timestamp,
                    });
                    this.eventStore.pushEvent({
                        eventHref,
                        event: customClickEvent,
                    });
                }
            }
        }
        this.eventStore.pushEvent({
            eventHref,
            event,
        });
    }
}
function stringByteSize(str) {
    return new Blob([str]).size;
}
export function getDom0EventHandlers(node) {
    const handlers = [];
    for (let i = 0; i < CUSTOM_DOM_0_EVENT_HANDLERS.length; i++) {
        const type = CUSTOM_DOM_0_EVENT_HANDLERS[i];
        const listenerKey = type;
        if (typeof node[listenerKey] === "function") {
            handlers.push(listenerKey);
        }
    }
    return handlers;
}
